/** @jsx jsx */
import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import { Box, Button, Flex, jsx, Text } from 'theme-ui'
import ChevronIcon from '~/assets/images/icons/chevron.inline.svg'
import MinusIcon from '~/assets/images/icons/minus.inline.svg'
import PlusIcon from '~/assets/images/icons/plus.inline.svg'
import { useAria } from '~/hooks/components/use-aria'

const CollapsiblePanel = ({
  title,
  titleColor = 'black',
  titleLabel,
  titleSx = {},
  titleVariant = 'title5',
  children,
  iconStyle = 'chevron',
  initiallyCollapsed = true,
}) => {
  const { updateAriaLive } = useAria()
  const [collapsed, setCollapsed] = useState(initiallyCollapsed)

  const toggleCollapse = () => {
    updateAriaLive(`${collapsed ? 'Expanded' : 'Collapsed'} ${title}.`)
    setCollapsed(!collapsed)
  }

  return (
    <Box>
      <Button
        variant="reset"
        aria-label={`${title}. Click to ${collapsed ? 'expand' : 'collapse'}.`}
        onClick={toggleCollapse}
        px={1}
        py={2}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Flex>
          <Text
            as="p"
            mr={2}
            variant={titleVariant}
            sx={{ textAlign: 'left', ...titleSx }}
          >
            {title}
          </Text>
          {titleLabel && (
            <Text
              as="span"
              ml={1}
              variant="description"
              sx={{
                color: 'brandTertiary',
                position: 'relative',
                top: '3px',
              }}
            >
              {titleLabel}
            </Text>
          )}
        </Flex>

        {iconStyle === 'chevron' && (
          <ChevronIcon
            sx={{
              height: '10px',
              pointerEvents: 'none',
              position: 'relative',
              top: '1px',
              transform: collapsed ? null : 'rotate(180deg)',
              width: '16px',
            }}
          />
        )}
        {iconStyle === 'add' && (
          <Fragment>
            {collapsed && <PlusIcon sx={{ pointerEvents: 'none' }} />}
            {!collapsed && <MinusIcon sx={{ pointerEvents: 'none' }} />}
          </Fragment>
        )}
      </Button>
      <Box
        data-testid="collapsible-content"
        pb={4}
        pt={1}
        px={1}
        sx={{
          display: collapsed ? 'none' : 'block',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

CollapsiblePanel.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  initiallyCollapsed: PropTypes.bool,
  titleColor: PropTypes.string,
}

export default CollapsiblePanel
